import React from 'react';
import MatchStatusBadge from './MatchStatusBadge';
import {
	formatLocation,
	getDisplayConfidence,
	getApplicableZoneFields,
	getEffectiveOrderReviewState,
	getImportRowIdentity,
	getIssueMessage,
	getLocationPresentation,
	getLocationMatch,
	getOrderIdentifier,
	getSourceValues,
	getAssignmentPresentation,
	LOCATION_PRESENTATION_STATE,
} from './importReview.model';
import styles from './ImportReview.module.css';

const getZoneLabel = field => {
	if (field?.id === 'contractZoneId') return 'Zona de contrato';
	if (field?.id === 'orderZoneId') return 'Zona de orden';
	return field?.name || 'Ubicación';
};

const formatConfidence = confidence => {
	const value = Number(confidence);
	if (!Number.isFinite(value)) return;
	return `${Math.round(value <= 1 ? value * 100 : value)}%`;
};

const ImportReviewTable = ({
	orders,
	fields,
	odtIdField,
	locationFields,
	assignedTechIdField,
	frequencyCreationSelections = [],
	onOpenOrder,
	isSubmitting = false,
}) => {
	if (!orders.length) {
		return (
			<div className={styles.emptyState} role='status'>
				No hay órdenes que coincidan con este filtro.
			</div>
		);
	}

	const zoneFields = Array.isArray(locationFields?.zoneFields)
		? locationFields.zoneFields
		: locationFields?.zoneIdField
			? [locationFields.zoneIdField]
			: [undefined];

	return (
		<div className={styles.tableScroll}>
			<table className={styles.reviewTable}>
				<caption className={styles.srOnly}>
					Órdenes importadas con estado, incidencias y comparación de ubicación
				</caption>
				<thead>
					<tr>
						<th scope='col'>Estado</th>
						<th scope='col'>Orden</th>
						<th scope='col'>Problemas</th>
						<th scope='col'>Técnico</th>
						<th scope='col'>Ubicación</th>
						<th scope='col'>Acción</th>
					</tr>
				</thead>
				<tbody>
					{orders.map((order, index) => {
						const orderZoneFields = getApplicableZoneFields(order, zoneFields);
						const effectiveReview = getEffectiveOrderReviewState({
							order,
							fields,
							locationFields,
							contractFrequencyCreationSelections: frequencyCreationSelections,
						});
						const { issues } = effectiveReview;
						const primaryMatch = getLocationMatch(
							order,
							orderZoneFields[0]?.id,
						);
						const primaryPresentation = getLocationPresentation({
							order,
							...locationFields,
							zoneIdField: orderZoneFields[0],
						});
						const identifier = getOrderIdentifier(order, odtIdField);
						const rowKey =
							getImportRowIdentity(order) ||
							`${identifier}-${order?.importDiagnostics?.rowNumber || index}`;
						const assignment = getAssignmentPresentation(
							order,
							assignedTechIdField,
						);
						const frequencyIssue = issues.find(issue =>
							[
								'CONTRACT_FREQUENCY_REVIEW_REQUIRED',
								'CONTRACT_FREQUENCY_UNKNOWN',
							].includes(issue?.code),
						);
						const assignmentSteps = [
							['Archivo', assignment.source],
							...(assignment.existing
								? [['Asignación actual', assignment.existing]]
								: []),
							...(assignment.previousResolved
								? [['Revisado anteriormente', assignment.previousResolved]]
								: []),
							['Asignado por Synapse', assignment.resolved],
						];

						return (
							<tr key={rowKey}>
								<td>
									<MatchStatusBadge
										status={effectiveReview.status}
										confidence={
											primaryPresentation.state ===
											LOCATION_PRESENTATION_STATE.UNASSIGNED
												? undefined
												: getDisplayConfidence(primaryMatch)
										}
									/>
								</td>
								<td>
									<strong className={styles.orderId}>{identifier}</strong>
									{order?.importDiagnostics?.rowNumber && (
										<span className={styles.rowNumber}>
											Fila {order.importDiagnostics.rowNumber}
										</span>
									)}
								</td>
								<td>
									{issues.length ? (
										<>
											<strong>{`${issues.length} ${
												issues.length === 1 ? 'incidencia' : 'incidencias'
											}`}</strong>
											<span className={styles.issuePreview}>
												{getIssueMessage(frequencyIssue || issues[0], order)}
											</span>
										</>
									) : (
										<span className={styles.muted}>Sin incidencias</span>
									)}
								</td>
								<td>
									<div className={styles.assignmentPipeline}>
										{assignmentSteps.map(([label, party], stepIndex) => (
											<React.Fragment key={label}>
												{stepIndex > 0 && (
													<span
														className={styles.assignmentArrow}
														aria-hidden='true'
													>
														→
													</span>
												)}
												<div className={styles.assignmentParty}>
													<span className={styles.locationLabel}>{label}</span>
													<strong>{party?.name || '—'}</strong>
													{party?.workCode && <span>{party.workCode}</span>}
													{party?.supervisorName && (
														<span>Supervisor: {party.supervisorName}</span>
													)}
												</div>
											</React.Fragment>
										))}
									</div>
									{assignment.explanation && (
										<span className={styles.assignmentExplanation}>
											{assignment.explanation}
										</span>
									)}
								</td>
								<td>
									<div className={styles.locationStack}>
										{orderZoneFields.map((zoneIdField, zoneIndex) => {
											const source = getSourceValues(order, zoneIdField?.id);
											const fieldMatch = getLocationMatch(
												order,
												zoneIdField?.id,
											);
											const presentation = getLocationPresentation({
												order,
												...locationFields,
												zoneIdField,
											});
											const similarity =
												presentation.state ===
												LOCATION_PRESENTATION_STATE.UNASSIGNED
													? undefined
													: getDisplayConfidence(fieldMatch);
											return (
												<div
													className={styles.locationGroup}
													key={zoneIdField?.id || zoneIndex}
												>
													<div className={styles.locationTypeRow}>
														{orderZoneFields.length > 1 && (
															<strong className={styles.locationType}>
																{getZoneLabel(zoneIdField)}
															</strong>
														)}
														{formatConfidence(similarity) && (
															<span className={styles.locationConfidence}>
																{formatConfidence(similarity)} de similitud
															</span>
														)}
													</div>
													<div className={styles.locationComparison}>
														<div>
															<span className={styles.locationLabel}>
																Archivo
															</span>
															<span>{formatLocation(source)}</span>
														</div>
														<span
															aria-hidden='true'
															className={styles.locationArrow}
														>
															→
														</span>
														<div>
															<span className={styles.locationLabel}>
																{presentation.label}
															</span>
															<span>
																{presentation.state ===
																LOCATION_PRESENTATION_STATE.UNASSIGNED
																	? '—'
																	: formatLocation(presentation.location)}
															</span>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</td>
								<td>
									<button
										type='button'
										className={styles.linkButton}
										onClick={() => onOpenOrder(order)}
										disabled={isSubmitting}
										aria-label={`Revisar orden ${identifier}`}
									>
										Revisar
									</button>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

export default ImportReviewTable;
